/**
 * Lobbyists & advocacy widgets (screenshots/73; 67; S/M/L in 71): Toss-ups to work, Target list, Coalitions,
 * Opposition watch, Brief me. Toss-ups also appears as the office default's S tile (70).
 */
import { OvIcon } from "../icons";
import { More, OvChip, Overflow, Row, RowFace, Stat, fit } from "./parts";
import type { WidgetProps } from "./props";

/** Toss-ups (71): S count › M top names › L forecast meters › status and actions. */
export function TossUps({ feeds, size, w, h, group }: WidgetProps) {
  const t = feeds.tossUps;
  if (!t) return null;
  if (size === "S") {
    return group === "office"
      ? <Stat value={t.count} chip={{ text: "Toss-up", tone: "neutral" }} sub={`needs ${t.needs} · projected ${t.projected}`} />
      : t.since ? <Stat value={t.count} chip={{ text: t.since, tone: "warning" }} sub={`${t.item} · needs ${t.needs}`} />
      : <Stat value={t.count} sub={`needs ${t.needs} · projected ${t.projected}`} />;
  }
  if (size === "M") {
    const shown = t.members.slice(0, fit(h, 55));
    return (
      <div className="ov-rows ov-rows--spread">
        {shown.map((m) => <Row key={m.personId} lead={<RowFace name={m.name} photoUrl={m.photoUrl} size={24} />} title={m.name}
          href={`/dashboard/people/${m.personId}`} right={<span className="ov-row__value">{Math.round(m.p * 100)}% your way</span>} />)}
      </div>
    );
  }
  const meters = w >= 8;
  const shown = t.members.slice(0, fit(h, meters ? 67 : 56, 30));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((m) => (
        <Row key={m.personId} lead={<RowFace name={m.name} photoUrl={m.photoUrl} />} title={m.name} href={`/dashboard/people/${m.personId}`}
          sub={meters ? `Ward ${m.ward} · ${m.strength}` : `Ward ${m.ward} · ${Math.round(m.p * 100)}% your way`}
          right={<>
            {meters && <RangeMeter p={m.p} low={m.low} high={m.high} />}
            {meters && <span className="ov-row__pct">{Math.round(m.p * 100)}%</span>}
            {m.status && <span className="ov-row__chipcol"><OvChip chip={m.status} /></span>}
          </>} />
      ))}
      <More href={`/dashboard/legislation/${t.recordId ?? ""}`}>All {t.count} toss-ups</More>
    </div>
  );
}

/** A member's chance with its range (71 L): track, range band and a tick at the estimate. */
const RangeMeter = ({ p, low, high }: { p: number; low: number; high: number }) => (
  <span className="ov-range" aria-hidden>
    <span className="ov-range__band" style={{ left: `${low * 100}%`, width: `${Math.max(2, (high - low) * 100)}%` }} />
    <span className="ov-range__tick" style={{ left: `${p * 100}%` }} />
  </span>
);

export function TargetList({ feeds, h, size }: WidgetProps) {
  const ms = feeds.targetList?.members ?? [];
  if (size === "S") return <Stat value={ms.length} sub={ms.slice(0, 2).map((m) => m.name).join(" · ")} />;
  const shown = ms.slice(0, fit(h, 61, 30));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((m) => <Row key={m.personId} lead={<RowFace name={m.name} photoUrl={m.photoUrl} size={28} />} title={m.name} sub={m.sub} href={`/dashboard/people/${m.personId}`} />)}
      <More href="/dashboard/people?view=targets">Open target list</More>
    </div>
  );
}

export function Coalitions({ feeds, h }: WidgetProps) {
  const rows = feeds.coalitions?.rows ?? [];
  const shown = rows.slice(0, fit(h, 40));
  return (
    <div className="ov-rows">
      {shown.map((r) => <Row key={r.name} title={r.name} href={r.href} right={<span className="ov-row__value ov-row__value--muted">{r.sub}</span>} />)}
      <Overflow n={rows.length - shown.length} />
    </div>
  );
}

export function OppositionWatch({ feeds, h }: WidgetProps) {
  const f = feeds.oppositionWatch?.filings ?? [];
  const shown = f.slice(0, fit(h, 58));
  return (
    <div className="ov-rows">
      {shown.map((x, n) => (
        <Row key={n} href={x.href} lead={<span className="ov-row__icon"><OvIcon name="file-text" size={13} /></span>} title={x.text} sub={x.period} />
      ))}
      <Overflow n={f.length - shown.length} />
    </div>
  );
}

export function BriefMe({ feeds }: WidgetProps) {
  const b = feeds.briefMe;
  if (!b) return null;
  return (
    <div className="ov-rows">
      <Row title={b.title} sub={b.bottomLine} href={b.recordId ? `/dashboard/legislation/${b.recordId}/brief` : undefined} />
    </div>
  );
}
